import React from 'react';
import {
  Link,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import HomePage from '../components/App';

const GUIDES = [
  {
    slug: 'self-hosting-ai-agents-on-a-single-vps',
    tag: 'Infrastructure',
    title: 'Self-hosting AI agents on a single VPS.',
    desc: 'A pragmatic stack for running open-weight models behind your own domain — Ollama, vLLM, gVisor, and a Caddy reverse proxy.',
    time: '14 min read',
    updated: 'Apr 2026',
    content: [
      'You can run surprisingly capable AI agents on a single VPS if you prioritize reliability over novelty. The baseline stack I recommend is Caddy for TLS + reverse proxy, Ollama or vLLM for model serving, and sandboxed execution via gVisor.',
      'Start with one model and one workflow. Instrument latency, queue depth, and token throughput before scaling. Most teams overbuild too early and create operational drag they do not need.',
    ],
  },
  {
    slug: 'taiwan-gold-card-end-to-end',
    tag: 'Residency',
    title: 'The Taiwan Gold Card, end to end.',
    desc: 'How I applied, what the income proof actually requires, and why it beats Portugal\'s D7 if you work in tech.',
    time: '11 min read',
    updated: 'Mar 2026',
    content: [
      'The Gold Card process is straightforward if your documentation is clean. The main failure mode is vague income proof, not eligibility. Submit concise, verifiable evidence and keep your timeline expectations realistic.',
      'For tech workers, Taiwan offers a stronger practical setup than many alternatives: deep hardware ecosystem, fast internet, and low friction for daily life once you are in-country.',
    ],
  },
  {
    slug: 'nomad-tax-setup-that-survives-an-audit',
    tag: 'Tax',
    title: 'Nomad tax setup that survives an audit.',
    desc: 'Three years of country-hopping, two structures, one accountant who doesn\'t panic. Covers EU, US, and Asian sources.',
    time: '18 min read',
    updated: 'Feb 2026',
    content: [
      'If your setup cannot be explained in one clear diagram, it will not survive an audit. Keep source of income, contract entities, and tax residency evidence explicit and consistent.',
      'The goal is not aggressive optimization. The goal is durable compliance while maintaining mobility. Simplicity usually wins over cleverness here.',
    ],
  },
];

const BLOG_POSTS = [
  {
    slug: 'why-we-built-jinban-in-the-eu-first',
    date: '2026-04-22',
    title: 'Why we built jinban in the EU first.',
    excerpt: 'Sovereignty is a product feature, not a compliance line item.',
    content: [
      'Most teams treat sovereignty as legal overhead. We treated it as product surface area: where data lives, who controls model behavior, and how enterprise buyers verify trust.',
      'Building in the EU first forced sharper architecture decisions early. It slowed us down for a month and saved us quarters of migration pain later.',
    ],
  },
  {
    slug: 'a-week-of-remote-work-from-hualien',
    date: '2026-04-08',
    title: 'A week of remote work from Hualien.',
    excerpt: 'Three cafés, two earthquakes, one shipped feature.',
    content: [
      'Hualien was equal parts focused work and environmental chaos. We shipped because our process assumes interruptions: async specs, small PRs, and explicit handoffs.',
      'If your team requires perfect conditions to deliver, your system is fragile. Build the kind of operating rhythm that survives real life.',
    ],
  },
  {
    slug: 'the-case-against-zapier-in-2026',
    date: '2026-03-19',
    title: 'The case against Zapier in 2026.',
    excerpt: 'When AI agents move work, glue stops paying rent.',
    content: [
      'Automation glue was a great abstraction for rule-based workflows. Agentic systems make that abstraction thinner because the work itself can move, not just the fields.',
      'The practical shift: fewer brittle automations, more intent-level orchestration with guardrails.',
    ],
  },
  {
    slug: 'running-a-4-person-company-across-3-timezones',
    date: '2026-03-02',
    title: 'Notes on running a 4-person company across 3 timezones.',
    excerpt: 'Async-by-default, sync where it matters, never the inverse.',
    content: [
      'Async is not just sending fewer meetings. It is writing decisions clearly enough that anyone can execute without waiting for context to be retold.',
      'We reserve sync for conflict resolution, design spikes, and morale. Everything else should be legible in writing.',
    ],
  },
  {
    slug: 'self-hosted-ai-is-finally-good-enough',
    date: '2026-02-14',
    title: 'Self-hosted AI is finally good enough.',
    excerpt: 'My open-weights stack vs the API I was paying for.',
    content: [
      'For many production workloads, open-weight models are now “good enough” on cost/performance if you design the task layer carefully.',
      'The biggest gains came from prompt discipline and retrieval quality, not from chasing the newest model every week.',
    ],
  },
  {
    slug: 'leaving-san-francisco-again',
    date: '2026-01-28',
    title: 'Leaving San Francisco, again.',
    excerpt: 'The third time. The reasons changed; the conclusion didn\'t.',
    content: [
      'The question was never whether SF is talented — it is. The question was where my team could operate with more focus and less ambient burn.',
      'Leaving was less about rejection and more about fit. Different stage, different constraints, different map.',
    ],
  },
];

const PROJECTS = [
  {
    slug: 'jinban-ai',
    name: 'jinban.ai',
    desc: 'EU sovereign virtual workspace bringing humans + AI agents onto one board. Founder & CTO.',
    tag: 'Software',
    year: '2026',
    size: 'lg',
    link: 'jinban.ai',
    detail: [
      'Jinban is a collaborative workspace where teams and AI agents execute work in one shared operational layer. The platform emphasizes sovereignty and traceability from day one.',
      'My role spans architecture, product direction, and shipping core workflows with a small distributed team.',
    ],
  },
  {
    slug: 'jinka',
    name: 'Jinka',
    desc: 'Distributed task and contract platform for global teams.',
    tag: 'Software',
    year: '2024',
    size: 'md',
    link: 'jinka.dev',
    detail: [
      'Jinka started as an internal operating model and evolved into a lightweight system for distributed execution and contracts.',
    ],
  },
  {
    slug: 'pocket-coach',
    name: 'Pocket Coach',
    desc: 'iOS app · personalized fitness coaching with on-device LLM inference.',
    tag: 'Mobile',
    year: '2025',
    size: 'md',
    link: 'App Store',
    detail: [
      'A personal coaching app focused on privacy and low-friction daily routines. Inference runs on-device where possible.',
    ],
  },
  {
    slug: 'routines',
    name: 'Routines',
    desc: 'Android · habit-tracking with offline-first sync.',
    tag: 'Android',
    year: '2024',
    size: 'sm',
    link: 'Play Store',
    detail: ['An offline-first habit tracker built for unreliable connectivity and fast interactions.'],
  },
  {
    slug: 'devlog-zach-dev',
    name: 'devlog.zach.dev',
    desc: 'Static-site engine I wrote for my own writing. Markdown, RSS, no build step.',
    tag: 'Side projects',
    year: '2024',
    size: 'sm',
    link: 'github',
    detail: ['A no-build static writing system optimized for fast publishing and simple ownership.'],
  },
  {
    slug: 'gvisor-quickstart',
    name: 'gvisor-quickstart',
    desc: 'Open-source helper scripts for sandboxing untrusted code on bare-metal Kubernetes.',
    tag: 'Side projects',
    year: '2025',
    size: 'sm',
    link: 'github',
    detail: ['Operational scripts and docs to make secure sandboxing less painful in real deployments.'],
  },
  {
    slug: 'tiny-recorder',
    name: 'Tiny Recorder',
    desc: 'iOS · 1-tap voice memos with auto-transcription. 50k installs.',
    tag: 'Mobile',
    year: '2023',
    size: 'sm',
    link: 'App Store',
    detail: ['A one-tap recorder with automatic transcript generation and a minimal interface.'],
  },
  {
    slug: 'field-notes',
    name: 'Field Notes',
    desc: 'Android offline travel journal with map sync.',
    tag: 'Android',
    year: '2023',
    size: 'sm',
    link: 'Play Store',
    detail: ['Travel journaling app designed for intermittent connectivity and quick capture.'],
  },
];

function formatDate(date) {
  return new Date(date).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function SubpageNav() {
  return (
    <nav className="nav">
      <div className="container nav__inner">
        <Link className="nav__brand" to="/">
          <div className="nav__avatar">Z</div>
          <span>zach.dev</span>
        </Link>
        <div className="nav__links">
          <Link to="/now">/now</Link>
          <Link to="/guides">guides</Link>
          <Link to="/blog">writing</Link>
          <Link to="/portfolio">portfolio</Link>
        </div>
        <div className="nav__status">
          <span className="dot" />
          online
        </div>
      </div>
    </nav>
  );
}

function PageShell({ children }) {
  return (
    <>
      <SubpageNav />
      {children}
    </>
  );
}

function NowPage() {
  return (
    <PageShell>
      <div className="page">
        <div className="container">
          <div className="page__head">
            <div>
              <div className="page__label">/ now</div>
              <h1 className="page__title">What I\'m <em>up to</em> right now.</h1>
            </div>
          </div>
          <div className="now__grid">
            <div className="now__status">
              <div className="now__status-row">
                <span>Location</span>
                <div>
                  <strong>Taipei, Taiwan</strong>
                  <p>Architecting AI systems for jinban.ai · exploring Taipei night markets</p>
                </div>
              </div>
              <div className="now__status-row">
                <span>Building</span>
                <div><strong style={{ fontWeight: 500 }}>jinban.ai — EU sovereign virtual workspace</strong></div>
              </div>
              <div className="now__status-row">
                <span>Reading</span>
                <div><strong style={{ fontWeight: 500 }}>The Diamond Age · Neal Stephenson</strong></div>
              </div>
              <div className="now__status-row">
                <span>Mood</span>
                <div><strong style={{ fontWeight: 500, fontFamily: 'var(--font-serif)', fontStyle: 'italic' }}>caffeinated, contemplative</strong></div>
              </div>
              <div className="now__edit">
                <span className="now__edit-dot" /> updated automatically · pulls from /now.json
              </div>
            </div>
          </div>
        </div>
      </div>
    </PageShell>
  );
}

function GuidesPage() {
  return (
    <PageShell>
      <div className="page">
        <div className="container">
          <div className="page__head">
            <div>
              <div className="page__label">Guides</div>
              <h1 className="page__title">Long-form, <em>opinionated</em>, road-tested.</h1>
            </div>
            <Link className="page__more" to="/guides-all">all guides →</Link>
          </div>
          <div className="guides__grid">
            {GUIDES.map((guide) => (
              <Link key={guide.slug} className="guide-card" to={`/guides/${guide.slug}`}>
                <div className="guide-card__arrow">→</div>
                <div className="guide-card__tag">{guide.tag}</div>
                <div className="guide-card__title">{guide.title}</div>
                <div className="guide-card__desc">{guide.desc}</div>
                <div className="guide-card__meta">
                  <span>{guide.time}</span>
                  <span>{guide.updated}</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </PageShell>
  );
}

function GuidePage() {
  const { guideId } = useParams();
  const guide = GUIDES.find((item) => item.slug === guideId);

  if (!guide) {
    return <NotFoundPage />;
  }

  return (
    <PageShell>
      <div className="page">
        <div className="container">
          <div className="page__head">
            <div>
              <div className="page__label">{guide.tag}</div>
              <h1 className="page__title">{guide.title}</h1>
              <div className="page__meta">
                <span>{guide.time}</span>
                <span>·</span>
                <span>{guide.updated}</span>
              </div>
            </div>
            <Link className="page__more" to="/guides">← back to guides</Link>
          </div>
          <article className="blog-post">
            <div className="blog-post__content">
              {guide.content.map((paragraph, index) => (
                <p key={index}>{paragraph}</p>
              ))}
            </div>
          </article>
        </div>
      </div>
    </PageShell>
  );
}

function GuidesAllPage() {
  return (
    <PageShell>
      <div className="page">
        <div className="container">
          <div className="page__head">
            <div>
              <div className="page__label">All Guides</div>
              <h1 className="page__title">Complete collection of guides.</h1>
            </div>
            <Link className="page__more" to="/guides">← back to guides</Link>
          </div>
          <div className="guides__grid">
            {GUIDES.map((guide) => (
              <Link key={guide.slug} className="guide-card" to={`/guides/${guide.slug}`}>
                <div className="guide-card__arrow">→</div>
                <div className="guide-card__tag">{guide.tag}</div>
                <div className="guide-card__title">{guide.title}</div>
                <div className="guide-card__desc">{guide.desc}</div>
                <div className="guide-card__meta">
                  <span>{guide.time}</span>
                  <span>{guide.updated}</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </PageShell>
  );
}

function BlogPage() {
  const location = useLocation();
  const navigate = useNavigate();

  React.useEffect(() => {
    const legacyMatch = location.hash.match(/^#post-(\d+)$/);
    if (!legacyMatch) return;

    const legacyIndex = Number(legacyMatch[1]);
    const legacyPost = BLOG_POSTS[legacyIndex];
    if (legacyPost) {
      navigate(`/blog/${legacyPost.slug}`, { replace: true });
    }
  }, [location.hash, navigate]);

  return (
    <PageShell>
      <div className="page">
        <div className="container">
          <div className="page__head">
            <div>
              <div className="page__label">Writing</div>
              <h1 className="page__title">Notes from the <em>road</em> and the <em>terminal</em>.</h1>
            </div>
            <Link className="page__more" to="/archive">archive · RSS →</Link>
          </div>
          <div className="blog__list">
            {BLOG_POSTS.map((post) => (
              <Link key={post.slug} className="blog-row" to={`/blog/${post.slug}`}>
                <div className="blog-row__date">{formatDate(post.date)}</div>
                <div className="blog-row__title">{post.title}</div>
                <div className="blog-row__excerpt">{post.excerpt}</div>
                <div className="blog-row__arrow">→</div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </PageShell>
  );
}

function BlogPostPage() {
  const { postId } = useParams();
  const post = BLOG_POSTS.find((item) => item.slug === postId);

  if (!post) {
    return <NotFoundPage />;
  }

  return (
    <PageShell>
      <div className="page">
        <div className="container">
          <div className="page__head">
            <div>
              <div className="page__label">Writing</div>
              <h1 className="page__title">{post.title}</h1>
              <div className="page__meta">
                <span>{formatDate(post.date)}</span>
                <span>·</span>
                <span>{post.excerpt}</span>
              </div>
            </div>
            <Link className="page__more" to="/blog">← back to writing</Link>
          </div>
          <article className="blog-post">
            <div className="blog-post__content">
              {post.content.map((paragraph, index) => (
                <p key={index}>{paragraph}</p>
              ))}
            </div>
          </article>
        </div>
      </div>
    </PageShell>
  );
}

function ArchivePage() {
  return (
    <PageShell>
      <div className="page">
        <div className="container">
          <div className="page__head">
            <div>
              <div className="page__label">Archive</div>
              <h1 className="page__title">All posts, organized by date.</h1>
            </div>
            <Link className="page__more" to="/blog">← back to writing</Link>
          </div>
          <div className="blog__list">
            {BLOG_POSTS.map((post) => (
              <Link key={post.slug} className="blog-row" to={`/blog/${post.slug}`}>
                <div className="blog-row__date">{formatDate(post.date)}</div>
                <div className="blog-row__title">{post.title}</div>
                <div className="blog-row__excerpt">{post.excerpt}</div>
                <div className="blog-row__arrow">→</div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </PageShell>
  );
}

function FeedPage() {
  return (
    <PageShell>
      <div className="page">
        <div className="container">
          <div className="page__head">
            <div>
              <div className="page__label">RSS Feed</div>
              <h1 className="page__title">Subscribe to my writing.</h1>
            </div>
            <Link className="page__more" to="/blog">← back to writing</Link>
          </div>
          <div className="blog-post">
            <div className="blog-post__content">
              <p>Subscribe via RSS to get notified when I publish new posts.</p>
              <p><a href="/feed.xml">Open /feed.xml</a></p>
            </div>
          </div>
        </div>
      </div>
    </PageShell>
  );
}

function PortfolioPage() {
  return (
    <PageShell>
      <div className="page">
        <div className="container">
          <div className="page__head">
            <div>
              <div className="page__label">Portfolio</div>
              <h1 className="page__title">Things I\'ve <em>shipped</em>.</h1>
            </div>
          </div>
          <div className="portfolio__grid">
            {PROJECTS.map((project) => (
              <Link key={project.slug} to={`/portfolio/${project.slug}`} className={`port-card port-card--${project.size}`}>
                <div className="port-card__head">
                  <div className="port-card__tag">{project.tag}</div>
                  <div className="port-card__year">{project.year}</div>
                </div>
                <div className="port-card__title">{project.name}</div>
                <div className="port-card__desc">{project.desc}</div>
                <div className="port-card__foot">
                  <span className="port-card__link">{project.link} ↗</span>
                  <span>view →</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </PageShell>
  );
}

function PortfolioItemPage() {
  const { projectId } = useParams();
  const project = PROJECTS.find((item) => item.slug === projectId);

  if (!project) {
    return <NotFoundPage />;
  }

  return (
    <PageShell>
      <div className="page">
        <div className="container">
          <div className="page__head">
            <div>
              <div className="page__label">{project.tag}</div>
              <h1 className="page__title">{project.name}</h1>
              <div className="page__meta">
                <span>{project.year}</span>
                <span>·</span>
                <span>{project.link}</span>
              </div>
            </div>
            <Link className="page__more" to="/portfolio">← back to portfolio</Link>
          </div>
          <article className="blog-post">
            <div className="blog-post__content">
              <p>{project.desc}</p>
              {project.detail.map((paragraph, index) => (
                <p key={index}>{paragraph}</p>
              ))}
            </div>
          </article>
        </div>
      </div>
    </PageShell>
  );
}

function NotFoundPage() {
  return (
    <PageShell>
      <div className="page">
        <div className="container">
          <div className="page__head">
            <div>
              <div className="page__label">404</div>
              <h1 className="page__title">Page not found.</h1>
            </div>
            <Link className="page__more" to="/">← back home</Link>
          </div>
        </div>
      </div>
    </PageShell>
  );
}

function LegacyGuideRedirect() {
  const { legacyId } = useParams();
  const guide = GUIDES[Number(legacyId)];
  if (!guide) return <NotFoundPage />;
  return <Navigate to={`/guides/${guide.slug}`} replace />;
}

function LegacyBlogRedirect() {
  const { legacyId } = useParams();
  const post = BLOG_POSTS[Number(legacyId)];
  if (!post) return <NotFoundPage />;
  return <Navigate to={`/blog/${post.slug}`} replace />;
}

function LegacyProjectRedirect() {
  const { legacyId } = useParams();
  const project = PROJECTS[Number(legacyId)];
  if (!project) return <NotFoundPage />;
  return <Navigate to={`/portfolio/${project.slug}`} replace />;
}

function Subpages() {
  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/now" element={<NowPage />} />
      <Route path="/guides" element={<GuidesPage />} />
      <Route path="/guides-all" element={<GuidesAllPage />} />
      <Route path="/guides/guide-:legacyId" element={<LegacyGuideRedirect />} />
      <Route path="/guides/:guideId" element={<GuidePage />} />
      <Route path="/blog" element={<BlogPage />} />
      <Route path="/blog/post-:legacyId" element={<LegacyBlogRedirect />} />
      <Route path="/blog/:postId" element={<BlogPostPage />} />
      <Route path="/archive" element={<ArchivePage />} />
      <Route path="/feed" element={<FeedPage />} />
      <Route path="/portfolio" element={<PortfolioPage />} />
      <Route path="/portfolio/project-:legacyId" element={<LegacyProjectRedirect />} />
      <Route path="/portfolio/:projectId" element={<PortfolioItemPage />} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

export default Subpages;
